import React from "react";
import Grid from "@material-ui/core/Grid";
import List from "@material-ui/core/List";
import ListItemText from "@material-ui/core/ListItemText";
import { FormikProps } from "formik";
import { Values } from "@src/Tools/Values";
import { STPrimaryError } from "@src/Components/Pages/Main/Content/Locations/LocationForm/styles";
import {
  STPaper,
  STListItemIcon,
  STGridRoot,
  STTitle,
  STButton,
  STCheckbox,
  STListItem,
  STListItemText,
  STTypography,
} from "./styles";

interface IProps<T, ItemType> {
  formikProps: FormikProps<T>;
  fieldName: keyof T;
  keyAsTitle: keyof ItemType;
  keyAsValue: keyof ItemType;
  allItems: ItemType[];
  selectedItems: ItemType[];
  label: string;
}

export default function TransferList<T, ItemType>(props: IProps<T, ItemType>) {
  const compare = (a: ItemType, b: ItemType) => {
    if (a[props.keyAsTitle] < b[props.keyAsTitle]) {
      return -1;
    }
    if (a[props.keyAsTitle] > b[props.keyAsTitle]) {
      return 1;
    }
    return 0;
  };

  const not = (a: ItemType[], b: ItemType[]) => {
    return a.filter(value => !b.find(val => value["id"] === val["id"])).sort(compare);
  };

  const intersection = (a: ItemType[], b: ItemType[]) => {
    return a.filter(value => b.find(val => value["id"] === val["id"])).sort(compare);
  };

  const [checked, setChecked] = React.useState<ItemType[]>([]);
  const [left, setLeft] = React.useState<ItemType[]>(not(props.allItems, props.selectedItems).sort(compare));
  const [right, setRight] = React.useState<ItemType[]>(props.selectedItems.sort(compare));

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const handleToggle = (value: ItemType) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleAllRight = () => {
    const rightItems = right.concat(left).sort(compare);
    setRight(rightItems);
    props.formikProps.setFieldValue(
      props.fieldName as string,
      rightItems.map(item => item[props.keyAsValue])
    );
    setLeft([]);
  };

  const handleCheckedRight = () => {
    const rightItems = right.concat(leftChecked).sort(compare);
    setRight(rightItems);
    props.formikProps.setFieldValue(
      props.fieldName as string,
      rightItems.map(item => item[props.keyAsValue])
    );
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked).sort(compare));
    const rightItems = not(right, rightChecked);
    setRight(rightItems);
    props.formikProps.setFieldValue(
      props.fieldName as string,
      rightItems.map(item => item[props.keyAsValue])
    );
    setChecked(not(checked, rightChecked));
  };

  const handleAllLeft = () => {
    setLeft(left.concat(right).sort(compare));
    setRight([]);
    props.formikProps.setFieldValue(props.fieldName as string, []);
  };

  const customList = (items: ItemType[]) => (
    <STPaper elevation={2}>
      <List role="list">
        {items.map((value: ItemType, index: number) => {
          const labelId = `transfer-list-item-${value[props.keyAsValue]}-label`;

          return (
            <STListItem key={index} role="listitem" button onClick={handleToggle(value)}>
              <STListItemIcon>
                <STCheckbox
                  checked={Boolean(checked.find(val => val[props.keyAsValue] === value[props.keyAsValue]))}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{ "aria-labelledby": labelId }}
                />
              </STListItemIcon>
              <STListItemText id={labelId}>
                <STTypography>{value[props.keyAsTitle]}</STTypography>
              </STListItemText>
            </STListItem>
          );
        })}
      </List>
    </STPaper>
  );

  return (
    <>
      <STGridRoot container justify="center" alignItems="center">
        <Grid item>
          <STTitle>{`${Values.getLocalizationValue("available")} ${props.label.toLowerCase()}`}</STTitle>
          {customList(left)}
        </Grid>
        <Grid item>
          <Grid container direction="column" alignItems="center">
            <STButton
              variant="outlined"
              size="small"
              onClick={handleAllRight}
              disabled={left.length === 0}
              aria-label="move all right"
            >
              ≫
            </STButton>
            <STButton
              variant="outlined"
              size="small"
              onClick={handleCheckedRight}
              disabled={leftChecked.length === 0}
              aria-label="move selected right"
            >
              &gt;
            </STButton>
            <STButton
              variant="outlined"
              size="small"
              onClick={handleCheckedLeft}
              disabled={rightChecked.length === 0}
              aria-label="move selected left"
            >
              &lt;
            </STButton>
            <STButton
              variant="outlined"
              size="small"
              onClick={handleAllLeft}
              disabled={right.length === 0}
              aria-label="move all left"
            >
              ≪
            </STButton>
          </Grid>
        </Grid>
        <Grid item>
          <STTitle>{`${Values.getLocalizationValue("selected")} ${props.label.toLowerCase()}`}</STTitle>
          {customList(right)}
        </Grid>
      </STGridRoot>
      {props.formikProps.errors && props.formikProps.errors[props.fieldName] && (
        <STPrimaryError container justify="flex-end">
          {Values.getLocalizationValue(props.formikProps.errors[props.fieldName] as string)}
        </STPrimaryError>
      )}
    </>
  );
}
